import type { SagaIterator } from 'redux-saga';
import { call, select, takeEvery, takeLeading } from 'redux-saga/effects';

import { authSelectors } from '../auth';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import * as entitlementsActions from './entitlementsActions';
import { type Entitlement, type EntitlementProps, mkEntitlement } from './entitlementsTypes';

type EntitlementsConfig = resourceStoreTypes.QcsSyncResourcesConfig<EntitlementProps>;

// The shared sync helpers also support the standard resources envelope; this endpoint uses entitlements.
type EntitlementsSyncResponse = {
  data?: { entitlements?: EntitlementProps[]; resources?: EntitlementProps[] };
};

const transformResponseToEntitlements = (response?: EntitlementsSyncResponse | null): Entitlement[] => {
  if (!response?.data?.entitlements) {
    throw new Error('Missing entitlements in QCS response');
  }
  return response.data.entitlements.map((data) => mkEntitlement(data));
};

const mkEntitlementsConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<EntitlementProps>();
const mkResourceConfig = (
  props: Partial<resourceStoreTypes.QcsSyncResourcesConfigProps<EntitlementProps>>,
): EntitlementsConfig =>
  mkEntitlementsConfig({
    resourceName: 'entitlements',
    resourceBaseUrl: '/entitlements',
    axiosConfig: { headers: { 'qcs-dataset-id': null } },
    transformResponseToResources: transformResponseToEntitlements,
    ...props,
  });

export function* getEntitlements(
  config: EntitlementsConfig,
  action: ReturnType<typeof entitlementsActions.getEntitlements>,
): SagaIterator {
  const datasetId: ReturnType<typeof authSelectors.getDatasetId> = yield select(authSelectors.getDatasetId);
  const getConfig = config.set('resourceBaseUrl', datasetId ? `/datasets/${datasetId}/entitlements` : '/entitlements');
  yield call(resourceSagaUtils.qcsSyncGetResources<EntitlementProps>, getConfig, action);
}

export function* getEntitlementsActionWatcher(): SagaIterator {
  const resourceConfig = mkResourceConfig({
    successAction: entitlementsActions.getEntitlementsSuccess,
    failureAction: entitlementsActions.getEntitlementsFailure,
  });
  yield takeLeading(entitlementsActions.getEntitlements.type, getEntitlements, resourceConfig);
}

export function* createEntitlementActionWatcher(): SagaIterator {
  const config = mkResourceConfig({
    transformResourceWithResponse: (_resource, response) => transformResponseToEntitlements(response),
    successAction: entitlementsActions.createEntitlementSuccess,
    failureAction: entitlementsActions.createEntitlementFailure,
  });
  yield takeEvery(
    entitlementsActions.createEntitlement.type,
    resourceSagaUtils.qcsSyncCreateResource<EntitlementProps>,
    config,
  );
}

export default [getEntitlementsActionWatcher, createEntitlementActionWatcher];
